import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { Wrench } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getInnerCardSurface } from '../../pages/MojeSmena/sheetStyle';
import { SheetButton } from '../../pages/MojeSmena/SheetUI';

const SECTION_COLOR = '#0CC0DF';

// POZOR: Field i StavOption MUSÍ být mimo parent — definovat dovnitř způsobí
// unmount/remount na každý render → focus skáče (viz TankovaniModal komentář).
function Field({ label, children, required, labelColor, hint }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                color: labelColor,
            }}>
                {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
            </span>
            {children}
            {hint && <span style={{ fontSize: 10, color: labelColor, lineHeight: 1.4 }}>{hint}</span>}
        </label>
    );
}

const STAV_OPTIONS = [
    { value: 'planovano', label: 'Plánováno', color: '#9CA3AF' },
    { value: 'probiha',   label: 'Probíhá',   color: '#FFC857' },
    { value: 'dokonceno', label: 'Hotovo',    color: '#10B981' },
];

/**
 * Rychlý modal pro zaznamenání servisu/opravy/STK kontroly. Otevírá se z karty
 * vozidla v Inspectoru (tlačítko "Servis" v sekci akcí). Pole jsou předvyplněná
 * podle kontextu.
 *
 * Po uložení s `stav=dokonceno` + `km_stav` se přepočítá `vozidlo.km_do_servisu`
 * (vychází z posledního dokončeného servisu) — progress bar v Přehledu se sám
 * obnoví.
 */
export default function ServisModal({ vozidlo, onClose, onSaved, isDark, isMobile = false }) {
    const [typySer, setTypySer] = useState([]);
    // typIds = pole ID vybraných úkonů (multi). První je "primární" pro display.
    const [typIds, setTypIds] = useState([]);
    // restartuje = explicitní volba; pre-fill smart default odvozený od typů.
    // null = user ještě nezasáhl, používá smart default. true/false = manual override.
    const [restartujeManual, setRestartujeManual] = useState(null);
    const [form, setForm] = useState(() => {
        const today = new Date().toISOString().slice(0, 10);
        return {
            stav: 'dokonceno',
            datum_od: today,
            datum_do: today,
            nazev_servisu: '',
            km_stav: vozidlo?.km_stav ? String(vozidlo.km_stav) : '',
            cena_kc: '',
            popis: '',
            poznamka: '',
        };
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/flotila/ciselniky')
            .then((r) => r.json())
            .then((d) => {
                if (cancelled) return;
                const types = Array.isArray(d?.typy_servisu) ? d.typy_servisu : [];
                setTypySer(types);
                // Default první typ — uživatel ho může odznačit nebo vybrat víc
                if (types.length > 0) {
                    setTypIds((prev) => prev.length > 0 ? prev : [types[0].id]);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const change = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
    };

    // Smart default — jakmile vybrané typy obsahují alespoň jednu intervalovou
    // údržbu, checkbox restartuje_interval je předvyplněný true. Ale uživatel
    // může explicitně override (restartujeManual !== null).
    const smartRestart = typIds.some((id) => {
        const t = typySer.find((x) => x.id === id);
        return !!t?.je_intervalova_udrzba;
    });
    const restartujeInterval = restartujeManual !== null ? restartujeManual : smartRestart;

    const submit = async (e) => {
        e.preventDefault();
        if (typIds.length === 0) {
            setError('Vyber alespoň jeden typ servisu / úkonu.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                vozidlo_id: vozidlo.id,
                typ_servisu_id: typIds[0], // primární = první vybraný
                typ_servisu_ids: typIds,
                stav: form.stav,
                restartuje_interval: form.stav === 'dokonceno' ? restartujeInterval : false,
                datum_od: form.datum_od,
                datum_do: form.datum_do || null,
                km_stav: form.km_stav ? Number(form.km_stav) : null,
                nazev_servisu: form.nazev_servisu || null,
                cena_kc: form.cena_kc ? Number(form.cena_kc) : null,
                popis: form.popis || null,
                poznamka: form.poznamka || null,
            };
            const r = await fetch('/api/flotila/servis', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const firstErr = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstErr || data?.message || data?.error || 'Uložení selhalo');
            }
            const saved = await r.json();
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            setError(err.message || 'Uložení selhalo');
        } finally {
            setSaving(false);
        }
    };

    const _colors = getTextColors(isDark);
    const _inner = getInnerCardSurface(isDark);
    const labelColor = _colors.textMuted;
    const subtle = _colors.textSecondary;
    const textColor = _colors.textPrimary;

    // Sjednocený inputStyle podle SheetInput tokens (h:44, body font, border 10).
    const inputStyle = {
        width: '100%',
        height: SHEET_TOKENS.sizes.inputHeight,
        background: _inner.background,
        color: textColor,
        border: `1px solid ${_inner.border.split(' ').pop()}`,
        borderRadius: SHEET_TOKENS.radius.input,
        padding: '0 12px',
        fontSize: SHEET_TOKENS.typography.body.size,
        fontWeight: SHEET_TOKENS.typography.body.weight,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const monoInput = { ...inputStyle, fontVariantNumeric: 'tabular-nums' };

    // Native dropdown options dědí systémové barvy — v dark módu by byly
    // bílé na bílém. Inline style na <option> to přebije.
    const optionStyle = {
        background: isDark ? '#1a1a1a' : '#fff',
        color: isDark ? '#fff' : '#111',
    };

    const podtitul = [vozidlo?.spz, vozidlo?.model].filter(Boolean).join(' · ');
    return (
        <ModalShell onClose={onClose} isDark={isDark} ikona={Wrench} barva={SECTION_COLOR} nadpis="Nový servis" sirka={480}>
            <form onSubmit={submit} style={{
                padding: SHEET_TOKENS.spacing.sheetPadX, display: 'flex', flexDirection: 'column', gap: 12, color: textColor,
            }}>
                {podtitul && <p style={{ margin: 0, fontSize: 12, color: labelColor }}>{podtitul}</p>}

                    {/* Stav — segmentovaný výběr (vizuálně lepší než dropdown pro 3 možnosti) */}
                    <Field label="Stav" required labelColor={labelColor}>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {STAV_OPTIONS.map((s) => {
                                const active = form.stav === s.value;
                                return (
                                    <button
                                        key={s.value}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, stav: s.value }))}
                                        style={{
                                            flex: 1,
                                            padding: '8px 10px', borderRadius: 7,
                                            background: active ? `${s.color}22` : 'transparent',
                                            color: active ? s.color : subtle,
                                            border: `1px solid ${active ? `${s.color}88` : isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            transition: 'background 0.12s, color 0.12s',
                                        }}
                                    >
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field
                        label="Typ servisu / úkony"
                        required
                        labelColor={labelColor}
                        hint="Můžeš vybrat víc úkonů — typicky jeden servis kombinuje několik (např. olej + brzdy + prohlídka)."
                    >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {typySer.map((t) => {
                                const active = typIds.includes(t.id);
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => {
                                            setTypIds((prev) => prev.includes(t.id)
                                                ? prev.filter((x) => x !== t.id)
                                                : [...prev, t.id]);
                                        }}
                                        style={{
                                            padding: '6px 10px', borderRadius: 999,
                                            background: active ? `${SECTION_COLOR}22` : 'transparent',
                                            color: active ? SECTION_COLOR : subtle,
                                            border: `1px solid ${active ? `${SECTION_COLOR}88` : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`,
                                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            transition: 'background 0.12s, color 0.12s',
                                        }}
                                    >
                                        {active ? '✓ ' : ''}{t.nazev}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Název / popis krátce" labelColor={labelColor}>
                        <input className="cockpit-input" style={inputStyle} value={form.nazev_servisu}
                            onChange={change('nazev_servisu')} maxLength={200}
                            placeholder="Např. Výměna oleje a filtru" />
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field label="Datum od" required labelColor={labelColor}>
                            <input className="cockpit-input" style={inputStyle} type="date" value={form.datum_od}
                                onChange={change('datum_od')} required />
                        </Field>
                        <Field label="Datum do" labelColor={labelColor}>
                            <input className="cockpit-input" style={inputStyle} type="date" value={form.datum_do}
                                onChange={change('datum_do')} min={form.datum_od} />
                        </Field>
                    </div>

                    {/* Checkbox: restartovat servisní interval. Smart default = true,
                        když některý vybraný úkon je intervalová údržba (prohlídka, olej).
                        Jen u dokončených servisů má smysl. */}
                    {form.stav === 'dokonceno' && (
                        <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '10px 12px', borderRadius: 7,
                            background: restartujeInterval ? 'rgba(16,185,129,0.08)' : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                            border: `1px solid ${restartujeInterval ? 'rgba(16,185,129,0.30)' : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`,
                            cursor: 'pointer',
                            transition: 'background 0.12s, border-color 0.12s',
                        }}>
                            <input
                                type="checkbox"
                                checked={restartujeInterval}
                                onChange={(e) => setRestartujeManual(e.target.checked)}
                                style={{ marginTop: 2, accentColor: '#10B981', cursor: 'pointer' }}
                            />
                            <span style={{ flex: 1 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                                    Tímto servisem restartovat servisní interval
                                </span>
                                <span style={{ display: 'block', fontSize: 11, color: subtle, marginTop: 2, lineHeight: 1.4 }}>
                                    Po dokončení se počítadlo „do dalšího servisu" přepočítá od nově zadaného tachometru.
                                    {smartRestart && restartujeManual === null && ' Předvyplněno dle vybraných úkonů.'}
                                </span>
                            </span>
                        </label>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field
                            label="Tachometr (km)"
                            labelColor={labelColor}
                        >
                            <input className="cockpit-input" style={monoInput} type="number" min="0" inputMode="numeric"
                                value={form.km_stav} onChange={change('km_stav')} />
                        </Field>
                        <Field label="Cena (Kč)" labelColor={labelColor}>
                            <input className="cockpit-input" style={monoInput} type="number" step="0.01" min="0" inputMode="decimal"
                                value={form.cena_kc} onChange={change('cena_kc')}
                                placeholder={form.stav === 'planovano' ? 'odhad' : ''} />
                        </Field>
                    </div>

                    <Field label="Popis (volitelně podrobně)" labelColor={labelColor}>
                        <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                            value={form.popis} onChange={change('popis')}
                            placeholder="Co se na autě dělalo / co se má udělat" />
                    </Field>

                    <Field label="Poznámka" labelColor={labelColor}>
                        <input className="cockpit-input" style={inputStyle} value={form.poznamka}
                            onChange={change('poznamka')} maxLength={500} />
                    </Field>

                    {error && (
                        <div style={{
                            padding: '8px 12px', borderRadius: 7, fontSize: 12,
                            background: 'rgba(239,68,68,0.12)', color: '#EF4444',
                            border: '1px solid rgba(239,68,68,0.30)',
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                        <SheetButton
                            isDark={isDark}
                            variant="secondary"
                            onClick={onClose}
                            fullWidth={false}
                        >Zrušit</SheetButton>
                        <SheetButton
                            isDark={isDark}
                            variant="primary"
                            accent={SECTION_COLOR}
                            accent2="#5DCAA5"
                            type="submit"
                            busy={saving}
                            busyLabel="Ukládám…"
                            fullWidth={false}
                        >Uložit servis</SheetButton>
                    </div>
            </form>
        </ModalShell>
    );
}
